/**
 * The Status filter beside the Team queue's search: a small button that opens
 * a menu of the queue's statuses, any number chosen at once. A status draws
 * its stage pie in gray until it is chosen, then in its own colour with its
 * name in stronger ink; no checkboxes. The button names what is chosen ("Any",
 * "Needs you, Stopped", "3 statuses") with the chosen pies stacked before it.
 * Each status counts the tickets it would show under the current search.
 */
import { StagePie } from "../ui/stagePie.js";
import { MenuItem, MenuSeparator, MoreMenu } from "../queue/MoreMenu.js";
import { ChevronDownIcon } from "../queue/icons.js";
import { cx } from "../queue/primitives.js";
import {
  STATUS_FILTER_GLYPH,
  STATUS_FILTER_LABEL,
  STATUS_FILTERS,
  statusFilterWords,
  type StatusFilter,
} from "./statusFilter.js";
import "./findBar.css";

/** How many chosen pies the button stacks before the words. */
const STACKED = 3;

export interface StatusFilterMenuProps {
  chosen: readonly StatusFilter[];
  /** Tickets each status would show under the current search. */
  counts: Record<StatusFilter, number>;
  onChange: (next: StatusFilter[]) => void;
}

function Glyph({ status, size }: { status: StatusFilter; size: number }) {
  const { stage, mode } = STATUS_FILTER_GLYPH[status];
  return <StagePie stage={stage} mode={mode} size={size} label={STATUS_FILTER_LABEL[status]} />;
}

export function StatusFilterMenu({ chosen, counts, onChange }: StatusFilterMenuProps) {
  const toggle = (status: StatusFilter) =>
    onChange(
      chosen.includes(status)
        ? chosen.filter((entry) => entry !== status)
        : STATUS_FILTERS.filter((entry) => entry === status || chosen.includes(entry)),
    );
  return (
    <MoreMenu
      align="right"
      className="sfmenu"
      label="Status"
      trigger={({ open, toggle: toggleMenu }) => (
        <button
          type="button"
          className={cx("sfbtn", chosen.length > 0 && "set")}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggleMenu}
          data-testid="team-status-filter"
        >
          <span className="k">Status</span>
          {chosen.length > 0 && (
            <span className="stack" aria-hidden>
              {chosen.slice(0, STACKED).map((status) => (
                <Glyph key={status} status={status} size={18} />
              ))}
            </span>
          )}
          <span className="words">{statusFilterWords(chosen)}</span>
          <ChevronDownIcon />
        </button>
      )}
    >
      <div data-keep-open className="sfopts">
        {STATUS_FILTERS.map((status) => {
          const on = chosen.includes(status);
          return (
            <button
              key={status}
              type="button"
              role="menuitemcheckbox"
              aria-checked={on}
              className={cx("sfopt", on && "on")}
              onClick={() => toggle(status)}
            >
              <span className="g" aria-hidden>
                <Glyph status={status} size={20} />
              </span>
              {STATUS_FILTER_LABEL[status]}
              <span className="k">{counts[status]}</span>
            </button>
          );
        })}
      </div>
      <MenuSeparator />
      <MenuItem onSelect={() => onChange([])}>Any status</MenuItem>
    </MoreMenu>
  );
}
